const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

export const asText = (value, fallback = '', maxLength = 600) => {
  if (typeof value !== 'string') return fallback;
  const clean = value.trim();
  return clean ? clean.slice(0, maxLength) : fallback;
};

export const asBoolean = (value, fallback = false) =>
  typeof value === 'boolean' ? value : fallback;

export const asInteger = (value, fallback = 0, min = 0, max = Number.MAX_SAFE_INTEGER) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, Math.round(number)));
};

export const asMoney = (value, fallback = null) => {
  if (value === null || value === '' || typeof value === 'undefined') return fallback;
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? Math.round(number * 100) / 100 : fallback;
};

export const asUrl = (value, fallback = '') => {
  const text = asText(value, '', 900);
  if (!text) return fallback;
  if (text.startsWith('/') || /^https?:\/\//i.test(text)) return text;
  return fallback;
};

export const asColor = (value, fallback = '') =>
  /^#[0-9a-f]{6}$/i.test(asText(value, '', 7)) ? value.toUpperCase() : fallback;

export const asStringList = (value, fallback = []) => {
  if (!Array.isArray(value)) return fallback;
  return value.map((item) => asText(item)).filter(Boolean);
};

export const deepMerge = (defaults, input) => {
  if (Array.isArray(defaults)) return Array.isArray(input) ? input : defaults;
  if (!isRecord(defaults)) return typeof input === typeof defaults ? input : defaults;
  const source = isRecord(input) ? input : {};
  return Object.fromEntries(
    Object.entries(defaults).map(([key, fallback]) => [key, deepMerge(fallback, source[key])])
  );
};

const DEFAULT_SITE = {
  brand: {
    name: "KRIAM's",
    nameUpper: "KRIAM'S",
    tagline: 'OurCake • YourStory',
    signature: 'by Chaitu Reddy',
    logo: '/images/kriams/kriams-logo-redesign.svg',
    logoLegacy: '/images/kriams/kriams-logo.png',
    logoAlt: "KRIAM's bakery logo"
  },
  catalog: {
    categories: [
      { id: 'all', label: 'All' },
      { id: 'brownies', label: 'Brownies' },
      { id: 'cakes', label: 'Cakes' },
      { id: 'cookies', label: 'Cookies' },
      { id: 'special', label: 'Specials' },
      { id: 'gifts', label: 'Gifts' }
    ]
  },
  navigation: {
    links: [
      { id: 'menu', label: 'Menu', target: 'menu', fallbackTargets: ['hero'], placement: 'left', mobile: true, emphasis: false },
      { id: 'story', label: 'Our Story', target: 'about', fallbackTargets: ['custom-order', 'hero'], placement: 'left', mobile: true, emphasis: false },
      { id: 'gallery', label: 'Gallery', target: 'gallery', fallbackTargets: ['menu', 'hero'], placement: 'right', mobile: true, emphasis: false },
      { id: 'order', label: 'Order Online', target: 'order', fallbackTargets: ['custom-order', 'menu', 'hero'], placement: 'right', mobile: true, emphasis: true }
    ],
    mobileCtaLabel: 'Order',
    mobileCtaTarget: 'order',
    mobileCtaFallbackTargets: ['custom-order', 'menu', 'hero']
  },
  appearance: {
    paper: '#FBF7F1',
    surface: '#FFFFFF',
    ink: '#1F1511',
    inkSoft: '#5D4F47',
    accentInk: '#8A6234',
    accentLine: '#B98A54',
    blushWhisper: '#F7E3E6',
    blushMid: '#F3CDD6',
    roseInk: '#B04A63'
  },
  contact: {
    whatsappNumber: '919717401350',
    phoneDisplay: '+91 97174 01350',
    instagramUrl: 'https://www.instagram.com/kriams_ourcake_yourstory/',
    instagramHandle: '@kriams_ourcake_yourstory',
    email: '',
    address: 'Kokapet, Hyderabad',
    pickupLabel: 'Kokapet pickup',
    serviceArea: 'Hyderabad',
    deliveryAreas: [],
    mapEmbedUrl: 'https://maps.google.com/maps?q=Kokapet%2C%20Hyderabad&z=14&output=embed',
    mapSearchUrl: 'https://www.google.com/maps/search/Kokapet+Hyderabad',
    hours: ['Orders confirmed on WhatsApp']
  },
  ordering: {
    currency: 'INR',
    maxQuantity: 20,
    defaultLeadTime: 'Please allow 24 hours',
    customLeadTime: 'Please allow 24–48 hours',
    deliveryConfirmation: 'Delivery availability and fee are confirmed before baking.',
    paymentNote: 'Payment details are shared after confirmation on WhatsApp.',
    confirmationNote: 'Your order is confirmed only after a WhatsApp reply.',
    allergyNotice: 'Please share allergies before ordering.',
    pickupMessage: "Pickup from KRIAM's, Kokapet",
    quickOrderMessage: "Hi KRIAM's! Please share today's availability.",
    customOrderMessage: "Hi KRIAM's! I'd like to discuss a custom order.",
    timeSlots: ['Flexible']
  },
  legal: {
    fssaiStatus: 'FSSAI Registered',
    fssaiNumber: '',
    egglessNotice: 'Eggless options available on request'
  },
  seo: {
    siteUrl: '',
    title: "KRIAM's | OurCake • YourStory",
    description: "Small-batch bakes made to order in Kokapet, Hyderabad.",
    keywords: ["KRIAM's", 'bakery Kokapet'],
    shareImage: '/images/kriams/full-product-spread.jpg',
    themeColor: '#F3CDD6',
    priceRange: '₹₹'
  }
};

export const normalizeSite = (raw) => {
  const merged = deepMerge(DEFAULT_SITE, raw);
  const fssaiNumber = asText(merged.legal.fssaiNumber, '', 80);
  const categoryIds = new Set(['all', 'brownies', 'cakes', 'cookies', 'special', 'gifts']);
  const sectionIds = new Set(['hero', 'menu', 'about', 'gallery', 'order', 'custom-order', 'best-sellers']);
  const categories = (Array.isArray(merged.catalog.categories) ? merged.catalog.categories : [])
    .map((category) => ({
      id: categoryIds.has(category?.id) ? category.id : '',
      label: asText(category?.label, '', 40)
    }))
    .filter((category, index, list) => category.id && category.label && list.findIndex((item) => item.id === category.id) === index);
  const navigationLinks = (Array.isArray(merged.navigation.links) ? merged.navigation.links : [])
    .map((link) => {
      const id = asText(link?.id, '', 40).toLowerCase();
      const target = asText(link?.target, '', 80);
      return {
        id,
        label: asText(link?.label, '', 48),
        target: sectionIds.has(target) ? target : '',
        fallbackTargets: asStringList(link?.fallbackTargets).filter((fallback) => sectionIds.has(fallback)),
        placement: link?.placement === 'right' ? 'right' : 'left',
        mobile: asBoolean(link?.mobile, true),
        emphasis: asBoolean(link?.emphasis, false)
      };
    })
    .filter((link, index, list) => link.id && link.label && link.target && list.findIndex((item) => item.id === link.id) === index);
  return {
    brand: {
      ...merged.brand,
      name: asText(merged.brand.name, DEFAULT_SITE.brand.name, 80),
      nameUpper: asText(merged.brand.nameUpper, DEFAULT_SITE.brand.nameUpper, 80),
      tagline: asText(merged.brand.tagline, DEFAULT_SITE.brand.tagline, 100),
      signature: asText(merged.brand.signature, DEFAULT_SITE.brand.signature, 100),
      logo: asUrl(merged.brand.logo, DEFAULT_SITE.brand.logo),
      logoLegacy: asUrl(merged.brand.logoLegacy, DEFAULT_SITE.brand.logoLegacy),
      logoAlt: asText(merged.brand.logoAlt, DEFAULT_SITE.brand.logoAlt, 140)
    },
    catalog: {
      categories: categories.length ? categories : DEFAULT_SITE.catalog.categories
    },
    navigation: {
      links: navigationLinks.length ? navigationLinks : DEFAULT_SITE.navigation.links,
      mobileCtaLabel: asText(merged.navigation.mobileCtaLabel, DEFAULT_SITE.navigation.mobileCtaLabel, 48),
      mobileCtaTarget: sectionIds.has(merged.navigation.mobileCtaTarget)
        ? merged.navigation.mobileCtaTarget
        : DEFAULT_SITE.navigation.mobileCtaTarget,
      mobileCtaFallbackTargets: asStringList(merged.navigation.mobileCtaFallbackTargets)
        .filter((target) => sectionIds.has(target))
    },
    appearance: {
      paper: asColor(merged.appearance.paper, DEFAULT_SITE.appearance.paper),
      surface: asColor(merged.appearance.surface, DEFAULT_SITE.appearance.surface),
      ink: asColor(merged.appearance.ink, DEFAULT_SITE.appearance.ink),
      inkSoft: asColor(merged.appearance.inkSoft, DEFAULT_SITE.appearance.inkSoft),
      accentInk: asColor(merged.appearance.accentInk, DEFAULT_SITE.appearance.accentInk),
      accentLine: asColor(merged.appearance.accentLine, DEFAULT_SITE.appearance.accentLine),
      blushWhisper: asColor(merged.appearance.blushWhisper, DEFAULT_SITE.appearance.blushWhisper),
      blushMid: asColor(merged.appearance.blushMid, DEFAULT_SITE.appearance.blushMid),
      roseInk: asColor(merged.appearance.roseInk, DEFAULT_SITE.appearance.roseInk)
    },
    contact: {
      ...merged.contact,
      whatsappNumber: String(merged.contact.whatsappNumber || '').replace(/\D/g, '') || DEFAULT_SITE.contact.whatsappNumber,
      phoneDisplay: asText(merged.contact.phoneDisplay, DEFAULT_SITE.contact.phoneDisplay, 40),
      instagramUrl: asUrl(merged.contact.instagramUrl, DEFAULT_SITE.contact.instagramUrl),
      instagramHandle: asText(merged.contact.instagramHandle, DEFAULT_SITE.contact.instagramHandle, 80),
      email: asText(merged.contact.email, '', 120),
      address: asText(merged.contact.address, DEFAULT_SITE.contact.address, 240),
      pickupLabel: asText(merged.contact.pickupLabel, DEFAULT_SITE.contact.pickupLabel, 100),
      serviceArea: asText(merged.contact.serviceArea, DEFAULT_SITE.contact.serviceArea, 100),
      deliveryAreas: asStringList(merged.contact.deliveryAreas),
      mapEmbedUrl: asUrl(merged.contact.mapEmbedUrl, DEFAULT_SITE.contact.mapEmbedUrl),
      mapSearchUrl: asUrl(merged.contact.mapSearchUrl, DEFAULT_SITE.contact.mapSearchUrl),
      hours: asStringList(merged.contact.hours, DEFAULT_SITE.contact.hours)
    },
    ordering: {
      ...merged.ordering,
      currency: merged.ordering.currency === 'INR' ? 'INR' : DEFAULT_SITE.ordering.currency,
      maxQuantity: asInteger(merged.ordering.maxQuantity, 20, 1, 50),
      defaultLeadTime: asText(merged.ordering.defaultLeadTime, DEFAULT_SITE.ordering.defaultLeadTime, 120),
      customLeadTime: asText(merged.ordering.customLeadTime, DEFAULT_SITE.ordering.customLeadTime, 120),
      deliveryConfirmation: asText(merged.ordering.deliveryConfirmation, DEFAULT_SITE.ordering.deliveryConfirmation, 300),
      paymentNote: asText(merged.ordering.paymentNote, DEFAULT_SITE.ordering.paymentNote, 300),
      confirmationNote: asText(merged.ordering.confirmationNote, DEFAULT_SITE.ordering.confirmationNote, 300),
      allergyNotice: asText(merged.ordering.allergyNotice, DEFAULT_SITE.ordering.allergyNotice, 300),
      pickupMessage: asText(merged.ordering.pickupMessage, DEFAULT_SITE.ordering.pickupMessage, 140),
      quickOrderMessage: asText(merged.ordering.quickOrderMessage, DEFAULT_SITE.ordering.quickOrderMessage, 500),
      customOrderMessage: asText(merged.ordering.customOrderMessage, DEFAULT_SITE.ordering.customOrderMessage, 500),
      timeSlots: asStringList(merged.ordering.timeSlots, ['Flexible'])
    },
    legal: {
      ...merged.legal,
      fssaiStatus: asText(merged.legal.fssaiStatus, DEFAULT_SITE.legal.fssaiStatus, 100),
      fssaiNumber: /x{2,}|placeholder|todo/i.test(fssaiNumber) ? '' : fssaiNumber,
      egglessNotice: asText(merged.legal.egglessNotice, DEFAULT_SITE.legal.egglessNotice, 180)
    },
    seo: {
      ...merged.seo,
      siteUrl: asUrl(merged.seo.siteUrl, ''),
      title: asText(merged.seo.title, DEFAULT_SITE.seo.title, 80),
      description: asText(merged.seo.description, DEFAULT_SITE.seo.description, 180),
      keywords: asStringList(merged.seo.keywords, DEFAULT_SITE.seo.keywords),
      shareImage: asUrl(merged.seo.shareImage, DEFAULT_SITE.seo.shareImage),
      themeColor: /^#[0-9a-f]{6}$/i.test(merged.seo.themeColor) ? merged.seo.themeColor : DEFAULT_SITE.seo.themeColor,
      priceRange: asText(merged.seo.priceRange, DEFAULT_SITE.seo.priceRange, 20)
    }
  };
};

const DEFAULT_HOME = {
  sections: {
    brandIntro: true,
    hero: true,
    marquee: true,
    newDrop: true,
    kitchenStory: true,
    bestSellers: true,
    orderFacts: true,
    menu: true,
    order: true,
    customOrder: true,
    gallery: true,
    testimonials: true,
    founderStory: true,
    faq: true,
    instagram: true,
    footer: true,
    whatsappFloat: true
  },
  brandIntro: { durationMs: 1500, reducedMotionDurationMs: 0 },
  hero: {
    poster: '/images/kriams/hero-poster.webp',
    posterMobile: '/images/kriams/hero-poster-mobile.webp',
    posterAlt: "KRIAM's dessert spread",
    slides: [],
    videoEnabled: true,
    videoMp4: '/videos/kriams-hero.mp4',
    videoWebm: '/videos/kriams-hero.webm',
    videoMobileMp4: '/videos/kriams-hero-mobile.mp4',
    videoMobileWebm: '/videos/kriams-hero-mobile.webm',
    headlineLineOneAccent: 'Fresh',
    headlineLineOneText: 'Bakes',
    headlineLineTwoPrefix: 'for',
    headlineLineTwoAccent: 'Timeless',
    headlineLineTwoText: 'Celebrations',
    subtitle: 'Baked fresh to order.',
    primaryCtaLabel: 'Explore Now',
    primaryCtaTarget: 'menu',
    secondaryCtaLabel: 'Order Best Sellers',
    secondaryCtaTarget: 'order',
    scrollLabel: 'Scroll'
  },
  marquee: { items: ['Baked Fresh To Order'] },
  newDrop: {
    productId: 0,
    brandEyebrow: '',
    brandTitle: '',
    instagramCta: 'See the latest bakes',
    badge: '',
    eyebrow: 'Now baking',
    title: 'Fresh from the kitchen',
    body: '',
    baseImage: '/images/kriams/full-product-spread.jpg',
    revealImage: '/images/kriams/full-product-spread.jpg',
    imageAlt: 'Fresh bakery product',
    details: [],
    primaryCta: 'Order now',
    secondaryCta: 'Browse full menu',
    stories: []
  },
  kitchenStory: {
    image: '/images/kriams/founder-chef-professional.webp',
    imageAlt: 'Founder preparing a fresh batch',
    caption: 'Founder and baker',
    eyebrow: 'Founder and baker',
    title: 'Made by hand,',
    titleAccent: 'fresh for tomorrow.',
    body: '',
    facts: [],
    reelUrl: '',
    reelLabel: 'Watch the kitchen story'
  },
  bestSellers: { eyebrow: 'Most loved', title: 'The ones on repeat', subtitle: '', productIds: [] },
  orderFacts: { items: [], ctaLabel: 'Check delivery & pickup', ctaTarget: 'order' },
  menu: { eyebrow: 'Our Menu', title: 'Handcrafted with care', subtitle: '', occasions: [] },
  order: { eyebrow: 'Online Orders', title: 'Ready to order?', description: '', formIntro: '', steps: [], mapTitle: "KRIAM's", mapSubtitle: '' },
  customOrder: { eyebrow: 'Custom orders', title: 'Have something in mind?', description: '', examples: [], ctaLabel: 'Talk to us', image: '', imageAlt: '', caption: '' },
  gallery: { eyebrow: 'Gallery', title: 'Real bakes, real celebrations', subtitle: '', initialCount: 9 },
  testimonials: { eyebrow: 'Customer Stories', title: 'What They Say', autoAdvanceMs: 4500 },
  founderStory: { eyebrow: 'Our Story', title: 'Every Bite Tells', titleAccent: 'Your Story', paragraphs: [], mainImage: '', mainImageAlt: '', floatImage: '', floatImageAlt: '', features: [], instagramCta: 'Follow on Instagram' },
  faq: { eyebrow: 'Good to know', title: 'Questions, answered' },
  instagram: { eyebrow: 'On Instagram', title: 'Follow the bakes', ctaLabel: 'Follow on Instagram', tileCount: 6 },
  footer: { ctaEyebrow: "Let's bake your story", ctaTitle: 'Planning a celebration?', ctaLabel: 'Plan an Order', tagline: '', hoursLine: '', reviewLabel: 'Find us on Google', copyright: "© KRIAM's", closingLine: 'Handcrafted with care' }
};

const asTarget = (value, fallback) => {
  const target = asText(value, fallback, 80);
  return /^[a-z][a-z0-9-]*$/.test(target) ? target : fallback;
};

export const normalizeHomepage = (raw) => {
  const merged = deepMerge(DEFAULT_HOME, raw);
  const normalizeTextList = (value, fallback = []) => asStringList(value, fallback);
  return {
    sections: Object.fromEntries(
      Object.entries(DEFAULT_HOME.sections).map(([key, fallback]) => [key, asBoolean(merged.sections[key], fallback)])
    ),
    brandIntro: {
      durationMs: asInteger(merged.brandIntro.durationMs, 1500, 900, 3500),
      reducedMotionDurationMs: asInteger(merged.brandIntro.reducedMotionDurationMs, 0, 0, 300)
    },
    hero: {
      poster: asUrl(merged.hero.poster, DEFAULT_HOME.hero.poster),
      posterMobile: asUrl(merged.hero.posterMobile, DEFAULT_HOME.hero.posterMobile),
      posterAlt: asText(merged.hero.posterAlt, DEFAULT_HOME.hero.posterAlt, 180),
      slides: (Array.isArray(merged.hero.slides) ? merged.hero.slides : [])
        .map((slide) => ({
          image: asUrl(slide?.image, ''),
          alt: asText(slide?.alt, DEFAULT_HOME.hero.posterAlt, 180)
        }))
        .filter((slide) => slide.image),
      videoEnabled: asBoolean(merged.hero.videoEnabled, true),
      videoMp4: asUrl(merged.hero.videoMp4, ''),
      videoWebm: asUrl(merged.hero.videoWebm, ''),
      videoMobileMp4: asUrl(merged.hero.videoMobileMp4, ''),
      videoMobileWebm: asUrl(merged.hero.videoMobileWebm, ''),
      headlineLineOneAccent: asText(merged.hero.headlineLineOneAccent, DEFAULT_HOME.hero.headlineLineOneAccent, 60),
      headlineLineOneText: asText(merged.hero.headlineLineOneText, DEFAULT_HOME.hero.headlineLineOneText, 60),
      headlineLineTwoPrefix: asText(merged.hero.headlineLineTwoPrefix, DEFAULT_HOME.hero.headlineLineTwoPrefix, 40),
      headlineLineTwoAccent: asText(merged.hero.headlineLineTwoAccent, DEFAULT_HOME.hero.headlineLineTwoAccent, 60),
      headlineLineTwoText: asText(merged.hero.headlineLineTwoText, DEFAULT_HOME.hero.headlineLineTwoText, 60),
      subtitle: asText(merged.hero.subtitle, DEFAULT_HOME.hero.subtitle, 240),
      primaryCtaLabel: asText(merged.hero.primaryCtaLabel, DEFAULT_HOME.hero.primaryCtaLabel, 60),
      primaryCtaTarget: asTarget(merged.hero.primaryCtaTarget, DEFAULT_HOME.hero.primaryCtaTarget),
      secondaryCtaLabel: asText(merged.hero.secondaryCtaLabel, DEFAULT_HOME.hero.secondaryCtaLabel, 60),
      secondaryCtaTarget: asTarget(merged.hero.secondaryCtaTarget, DEFAULT_HOME.hero.secondaryCtaTarget),
      scrollLabel: asText(merged.hero.scrollLabel, DEFAULT_HOME.hero.scrollLabel, 30)
    },
    marquee: { items: normalizeTextList(merged.marquee.items, DEFAULT_HOME.marquee.items) },
    newDrop: {
      productId: asInteger(merged.newDrop.productId, 0, 1),
      brandEyebrow: asText(merged.newDrop.brandEyebrow, '', 100),
      brandTitle: asText(merged.newDrop.brandTitle, '', 140),
      instagramCta: asText(merged.newDrop.instagramCta, DEFAULT_HOME.newDrop.instagramCta, 80),
      badge: asText(merged.newDrop.badge, '', 60),
      eyebrow: asText(merged.newDrop.eyebrow, DEFAULT_HOME.newDrop.eyebrow, 80),
      title: asText(merged.newDrop.title, DEFAULT_HOME.newDrop.title, 140),
      body: asText(merged.newDrop.body, '', 700),
      baseImage: asUrl(merged.newDrop.baseImage, DEFAULT_HOME.newDrop.baseImage),
      revealImage: asUrl(merged.newDrop.revealImage, DEFAULT_HOME.newDrop.revealImage),
      imageAlt: asText(merged.newDrop.imageAlt, DEFAULT_HOME.newDrop.imageAlt, 180),
      details: (Array.isArray(merged.newDrop.details) ? merged.newDrop.details : [])
        .map((detail) => ({
          label: asText(detail?.label, '', 60),
          value: asText(detail?.value, '', 120)
        }))
        .filter((detail) => detail.label && detail.value),
      primaryCta: asText(merged.newDrop.primaryCta, DEFAULT_HOME.newDrop.primaryCta, 80),
      secondaryCta: asText(merged.newDrop.secondaryCta, DEFAULT_HOME.newDrop.secondaryCta, 80),
      stories: (Array.isArray(merged.newDrop.stories) ? merged.newDrop.stories : [])
        .map((story) => ({
          image: asUrl(story?.image, ''),
          eyebrow: asText(story?.eyebrow, '', 80),
          title: asText(story?.title, '', 120)
        }))
        .filter((story) => story.image && story.title)
    },
    kitchenStory: {
      image: asUrl(merged.kitchenStory.image, DEFAULT_HOME.kitchenStory.image),
      imageAlt: asText(merged.kitchenStory.imageAlt, DEFAULT_HOME.kitchenStory.imageAlt, 180),
      caption: asText(merged.kitchenStory.caption, DEFAULT_HOME.kitchenStory.caption, 120),
      eyebrow: asText(merged.kitchenStory.eyebrow, DEFAULT_HOME.kitchenStory.eyebrow, 80),
      title: asText(merged.kitchenStory.title, DEFAULT_HOME.kitchenStory.title, 100),
      titleAccent: asText(merged.kitchenStory.titleAccent, DEFAULT_HOME.kitchenStory.titleAccent, 100),
      body: asText(merged.kitchenStory.body, '', 900),
      facts: (Array.isArray(merged.kitchenStory.facts) ? merged.kitchenStory.facts : [])
        .map((fact) => ({ icon: asText(fact?.icon, 'sparkle', 40), label: asText(fact?.label, '', 100) }))
        .filter((fact) => fact.label),
      reelUrl: asUrl(merged.kitchenStory.reelUrl, ''),
      reelLabel: asText(merged.kitchenStory.reelLabel, DEFAULT_HOME.kitchenStory.reelLabel, 80)
    },
    bestSellers: {
      eyebrow: asText(merged.bestSellers.eyebrow, DEFAULT_HOME.bestSellers.eyebrow, 80),
      title: asText(merged.bestSellers.title, DEFAULT_HOME.bestSellers.title, 120),
      subtitle: asText(merged.bestSellers.subtitle, '', 300),
      productIds: (Array.isArray(merged.bestSellers.productIds) ? merged.bestSellers.productIds : [])
        .map((id) => asInteger(id, 0, 1))
        .filter(Boolean)
    },
    orderFacts: {
      items: (Array.isArray(merged.orderFacts.items) ? merged.orderFacts.items : [])
        .map((item) => ({
          icon: asText(item?.icon, 'sparkle', 40),
          value: asText(item?.value, '', 100),
          label: asText(item?.label, '', 140)
        }))
        .filter((item) => item.value),
      ctaLabel: asText(merged.orderFacts.ctaLabel, DEFAULT_HOME.orderFacts.ctaLabel, 80),
      ctaTarget: asTarget(merged.orderFacts.ctaTarget, DEFAULT_HOME.orderFacts.ctaTarget)
    },
    menu: {
      eyebrow: asText(merged.menu.eyebrow, DEFAULT_HOME.menu.eyebrow, 80),
      title: asText(merged.menu.title, DEFAULT_HOME.menu.title, 120),
      subtitle: asText(merged.menu.subtitle, '', 300),
      occasions: (Array.isArray(merged.menu.occasions) ? merged.menu.occasions : [])
        .map((occasion) => ({
          categoryId: asText(occasion?.categoryId, '', 40),
          label: asText(occasion?.label, '', 80),
          image: asUrl(occasion?.image, '')
        }))
        .filter((occasion) => occasion.categoryId && occasion.label && occasion.image)
    },
    order: {
      eyebrow: asText(merged.order.eyebrow, DEFAULT_HOME.order.eyebrow, 80),
      title: asText(merged.order.title, DEFAULT_HOME.order.title, 120),
      description: asText(merged.order.description, '', 500),
      formIntro: asText(merged.order.formIntro, '', 180),
      steps: (Array.isArray(merged.order.steps) ? merged.order.steps : [])
        .map((step) => ({ title: asText(step?.title, '', 100), body: asText(step?.body, '', 240) }))
        .filter((step) => step.title && step.body),
      mapTitle: asText(merged.order.mapTitle, DEFAULT_HOME.order.mapTitle, 120),
      mapSubtitle: asText(merged.order.mapSubtitle, '', 160)
    },
    customOrder: {
      eyebrow: asText(merged.customOrder.eyebrow, DEFAULT_HOME.customOrder.eyebrow, 80),
      title: asText(merged.customOrder.title, DEFAULT_HOME.customOrder.title, 140),
      description: asText(merged.customOrder.description, '', 500),
      examples: normalizeTextList(merged.customOrder.examples),
      ctaLabel: asText(merged.customOrder.ctaLabel, DEFAULT_HOME.customOrder.ctaLabel, 80),
      image: asUrl(merged.customOrder.image, ''),
      imageAlt: asText(merged.customOrder.imageAlt, '', 180),
      caption: asText(merged.customOrder.caption, '', 140)
    },
    gallery: {
      eyebrow: asText(merged.gallery.eyebrow, DEFAULT_HOME.gallery.eyebrow, 80),
      title: asText(merged.gallery.title, DEFAULT_HOME.gallery.title, 120),
      subtitle: asText(merged.gallery.subtitle, '', 300),
      initialCount: asInteger(merged.gallery.initialCount, 9, 1, 30)
    },
    testimonials: {
      eyebrow: asText(merged.testimonials.eyebrow, DEFAULT_HOME.testimonials.eyebrow, 80),
      title: asText(merged.testimonials.title, DEFAULT_HOME.testimonials.title, 120),
      autoAdvanceMs: asInteger(merged.testimonials.autoAdvanceMs, 4500, 3000, 20000)
    },
    founderStory: {
      eyebrow: asText(merged.founderStory.eyebrow, DEFAULT_HOME.founderStory.eyebrow, 80),
      title: asText(merged.founderStory.title, DEFAULT_HOME.founderStory.title, 100),
      titleAccent: asText(merged.founderStory.titleAccent, DEFAULT_HOME.founderStory.titleAccent, 100),
      paragraphs: normalizeTextList(merged.founderStory.paragraphs),
      mainImage: asUrl(merged.founderStory.mainImage, ''),
      mainImageAlt: asText(merged.founderStory.mainImageAlt, '', 180),
      floatImage: asUrl(merged.founderStory.floatImage, ''),
      floatImageAlt: asText(merged.founderStory.floatImageAlt, '', 180),
      features: (Array.isArray(merged.founderStory.features) ? merged.founderStory.features : [])
        .map((feature) => ({
          icon: asText(feature?.icon, 'sparkle', 40),
          title: asText(feature?.title, '', 80),
          description: asText(feature?.description, '', 160)
        }))
        .filter((feature) => feature.title),
      instagramCta: asText(merged.founderStory.instagramCta, DEFAULT_HOME.founderStory.instagramCta, 80)
    },
    faq: {
      eyebrow: asText(merged.faq.eyebrow, DEFAULT_HOME.faq.eyebrow, 80),
      title: asText(merged.faq.title, DEFAULT_HOME.faq.title, 120)
    },
    instagram: {
      eyebrow: asText(merged.instagram.eyebrow, DEFAULT_HOME.instagram.eyebrow, 80),
      title: asText(merged.instagram.title, DEFAULT_HOME.instagram.title, 120),
      ctaLabel: asText(merged.instagram.ctaLabel, DEFAULT_HOME.instagram.ctaLabel, 80),
      tileCount: asInteger(merged.instagram.tileCount, 6, 1, 12)
    },
    footer: {
      ctaEyebrow: asText(merged.footer.ctaEyebrow, DEFAULT_HOME.footer.ctaEyebrow, 100),
      ctaTitle: asText(merged.footer.ctaTitle, DEFAULT_HOME.footer.ctaTitle, 180),
      ctaLabel: asText(merged.footer.ctaLabel, DEFAULT_HOME.footer.ctaLabel, 80),
      tagline: asText(merged.footer.tagline, '', 400),
      hoursLine: asText(merged.footer.hoursLine, '', 180),
      reviewLabel: asText(merged.footer.reviewLabel, DEFAULT_HOME.footer.reviewLabel, 100),
      copyright: asText(merged.footer.copyright, DEFAULT_HOME.footer.copyright, 160),
      closingLine: asText(merged.footer.closingLine, DEFAULT_HOME.footer.closingLine, 120)
    }
  };
};

const AVAILABILITY = new Set(['fresh', 'made-to-order', 'preorder', 'sold-out']);
const EGGLESS = new Set(['available', 'on-request', 'unavailable']);
const CATEGORIES = new Set(['brownies', 'cakes', 'cookies', 'special', 'gifts']);

export const normalizeProducts = (raw, defaultLeadTime = 'Please allow 24 hours') => {
  const list = Array.isArray(raw?.products) ? raw.products : [];
  const seen = new Set();
  return list.reduce((products, item, index) => {
    const id = asInteger(item?.id, 0, 1);
    if (!id || seen.has(id)) return products;
    seen.add(id);
    const priceOnRequest = asBoolean(item?.priceOnRequest, Boolean(item?.orderOnly || item?.price == null));
    const price = priceOnRequest ? null : asMoney(item?.price, null);
    const photos = Array.isArray(item?.photos)
      ? item.photos.map((photo) => asUrl(photo, '')).filter(Boolean)
      : [];
    const image = asUrl(item?.image, photos[0] || '/images/kriams/full-product-spread.jpg');
    if (!photos.includes(image)) photos.unshift(image);
    const availability = AVAILABILITY.has(item?.availability) ? item.availability : 'made-to-order';
    const variants = Array.isArray(item?.variants)
      ? item.variants.map((variant, variantIndex) => {
          const variantPriceOnRequest = asBoolean(variant?.priceOnRequest, variant?.price == null);
          return {
            id: asText(variant?.id, `${id}-${variantIndex + 1}`, 80),
            name: asText(variant?.name, `Option ${variantIndex + 1}`, 100),
            price: variantPriceOnRequest ? null : asMoney(variant?.price, null),
            priceOnRequest: variantPriceOnRequest,
            active: asBoolean(variant?.active, true)
          };
        }).filter((variant) => variant.active)
      : [];
    products.push({
      ...item,
      id,
      active: asBoolean(item?.active, true),
      category: CATEGORIES.has(item?.category) ? item.category : 'special',
      name: asText(item?.name, `Product ${index + 1}`, 120),
      desc: asText(item?.desc, 'Freshly made to order.', 500),
      tags: asStringList(item?.tags),
      price,
      priceOnRequest: priceOnRequest || price === null,
      priceLabel: asText(item?.priceLabel, priceOnRequest || price === null ? 'Price on request' : '', 80),
      orderOnly: asBoolean(item?.orderOnly, priceOnRequest || price === null),
      image,
      photos,
      variants,
      availability,
      availabilityNote: asText(item?.availabilityNote, '', 180),
      leadTime: asText(item?.leadTime, defaultLeadTime, 120),
      leadTimeDays: asInteger(item?.leadTimeDays, availability === 'fresh' ? 0 : 1, 0, 30),
      eggless: EGGLESS.has(item?.eggless) ? item.eggless : 'on-request',
      allergens: asStringList(item?.allergens),
      featured: asBoolean(item?.featured, false),
      bestSeller: asBoolean(item?.bestSeller, false),
      newDrop: asBoolean(item?.newDrop, false),
      priority: asInteger(item?.priority, index + 1, 0, 999)
    });
    return products;
  }, []).sort((a, b) => a.priority - b.priority || a.id - b.id);
};

const normalizeDateBound = (value) => {
  const text = asText(value, '', 60);
  const time = text ? Date.parse(text) : NaN;
  return Number.isNaN(time) ? null : time;
};

export const normalizeOffers = (raw) => {
  const list = Array.isArray(raw?.offers) ? raw.offers : [];
  return list.map((offer, index) => ({
    id: asText(offer?.id, `offer-${index + 1}`, 80),
    enabled: asBoolean(offer?.enabled, false),
    title: asText(offer?.title, 'Offer', 100),
    valueLabel: asText(offer?.valueLabel, '', 80),
    announcement: asText(offer?.announcement, '', 240),
    ctaLabel: asText(offer?.ctaLabel, 'Order now', 60),
    code: asText(offer?.code, '', 40),
    minimumOrder: asMoney(offer?.minimumOrder, null),
    terms: asText(offer?.terms, '', 500),
    startsAt: asText(offer?.startsAt, '', 60),
    endsAt: asText(offer?.endsAt, '', 60),
    priority: asInteger(offer?.priority, index, 0, 999),
    placements: asStringList(offer?.placements),
    whatsappMessage: asText(offer?.whatsappMessage, '', 500)
  }));
};

export const isOfferActive = (offer, placement, now = Date.now()) => {
  if (!offer?.enabled || (placement && !offer.placements.includes(placement))) return false;
  const startsAt = normalizeDateBound(offer.startsAt);
  const endsAt = normalizeDateBound(offer.endsAt);
  return (startsAt === null || now >= startsAt) && (endsAt === null || now <= endsAt);
};

export const activeOfferFor = (offers, placement, now = Date.now()) =>
  offers
    .filter((offer) => isOfferActive(offer, placement, now))
    .sort((a, b) => b.priority - a.priority)[0] || null;

export const normalizeGallery = (raw) => ({
  images: (Array.isArray(raw?.images) ? raw.images : [])
    .map((item) => ({
      url: asUrl(item?.url, ''),
      label: asText(item?.label, 'KRIAM\'s bake', 160),
      span: ['wide', 'tall'].includes(item?.span) ? item.span : '',
      video: asUrl(item?.video, '')
    }))
    .filter((item) => item.url)
});

export const normalizeTestimonials = (raw) => ({
  testimonials: (Array.isArray(raw?.testimonials) ? raw.testimonials : [])
    .map((item) => ({
      name: asText(item?.name, 'Customer', 80),
      text: asText(item?.text, '', 700),
      rating: asInteger(item?.rating, 5, 1, 5),
      location: asText(item?.location, '', 100),
      source: asText(item?.source, '', 80),
      date: asText(item?.date, '', 40)
    }))
    .filter((item) => item.text)
});

export const normalizeFaq = (raw) => ({
  faqs: (Array.isArray(raw?.faqs) ? raw.faqs : [])
    .map((item) => ({ q: asText(item?.q, '', 240), a: asText(item?.a, '', 1000) }))
    .filter((item) => item.q && item.a)
});
